import { datadogRum } from '@datadog/browser-rum';

import { useRootStore } from '../..';
import { arrayToMap } from './array_to_map';
import { isArray, isRecord, isString, isUndefined } from './type_guards';

export function startDatadog() {
  // Only run on Production
  if (
    window.location.host !== 'v3.myqsrsoft.com' &&
    window.location.host !== 'home.myqsrsoft.com' &&
    window.location.host !== 'myqsrsoft.com'
  ) {
    return;
  }

  datadogRum.init({
    applicationId: 'deb7407c-9620-43ed-851c-787671a06b20',
    clientToken: 'pub3cf6547dbabf8b52ca58f9920fe56294',
    site: 'datadoghq.com',
    service: 'myq_local',

    version: '3.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 25,
    trackUserInteractions: true,
    trackSessionAcrossSubdomains: true,
  });
}

export interface DatadogSendInput {
  action: string;
  actionData: Record<string, unknown>;
}

export function datadogSend(input: DatadogSendInput) {
  const { actionData, action } = input;
  const rootStore = useRootStore();

  const userData: Record<string, unknown> = rootStore.user ?? {};

  const orgData = rootStore.org ?? {};
  const userEmail =
    isRecord(orgData.displayData) && isString(orgData.displayData.email)
      ? orgData.displayData.email
      : '';
  const orgName = isString(orgData.orgName)
    ? orgData.orgName
    : 'Invalid Org Name';
  const clientId = `${orgData.clientId}` || 'null';

  const userGroups: Record<string, unknown>[] = (
    isArray(rootStore.userGroups) ? rootStore.userGroups : []
  )
    .map((g) => (isRecord(g) ? g : undefined))
    .filter((g) => !isUndefined(g));

  const roles = userGroups.map((g) => g.name);
  const rolesWithOrgName = roles.map((g) => `${g}-${orgName}`);

  const appsMap = arrayToMap(
    (orgData.apps ?? []).filter((a: unknown) => isString(a)),
    (a: string) => a,
  );
  const hasPermission = (p: string) => !isUndefined(appsMap[p]);

  const data = {
    ...actionData,
    userId: rootStore.userId,
    userName: userData.userName ?? '',
    userEmail,
    orgName,
    clientId,
    orgNameAndClientId: `${orgName} ${clientId}`,
    roles,
    rolesWithOrgName,
    appAccess: {
      forms: hasPermission('forms'),
      reports: hasPermission('reports'),
      invmobile: hasPermission('invmobile'),
      rbi: hasPermission('rbi'),
      security: hasPermission('security'),
      simplethink: hasPermission('simplethink'),
      datapass: hasPermission('datapass'),
      timekeeping: hasPermission('timekeeping'),
      payroll: hasPermission('payroll'),
      coachq: hasPermission('coachq'),
      hr: hasPermission('hr'),
      scheduling: hasPermission('scheduling'),
      employee_portal: hasPermission('employee_portal'),
      workforce: hasPermission('workforce'),
      equipment: hasPermission('equipment'),
      equipment_premium: hasPermission('equipment_premium'),
      qsrconnect: hasPermission('qsrconnect'),
      file_manager: hasPermission('file_manager'),
      candi_mt: hasPermission('candi_mt'),
      myq_ops: hasPermission('myq_ops'),
    },
  };

  // Can this throw?
  datadogRum.addAction(action, data);
}

export const send = datadogSend;
